import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const PlacementStatisticsTab = ({ dateRange }) => {
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [selectedInstitution, setSelectedInstitution] = useState('all');

  const programOptions = [
    { value: 'all', label: 'All Programs' },
    { value: 'btech', label: 'B.Tech' },
    { value: 'mtech', label: 'M.Tech' },
    { value: 'mba', label: 'MBA' },
    { value: 'mca', label: 'MCA' }
  ];

  const institutionOptions = [
    { value: 'all', label: 'All Institutions' },
    { value: 'inst1', label: 'Tech University' },
    { value: 'inst2', label: 'Engineering College' },
    { value: 'inst3', label: 'Management Institute' }
  ];

  const institutionPerformance = [
    {
      institution: 'Tech University',
      totalStudents: 850,
      placed: 748,
      placementRate: 88.0,
      avgPackage: '₹7.2 LPA',
      highestPackage: '₹24 LPA',
      topRecruiter: 'Tech Innovations'
    },
    {
      institution: 'Engineering College',
      totalStudents: 720,
      placed: 612,
      placementRate: 85.0,
      avgPackage: '₹6.5 LPA',
      highestPackage: '₹18 LPA',
      topRecruiter: 'DataTech Solutions'
    },
    {
      institution: 'Management Institute',
      totalStudents: 450,
      placed: 405,
      placementRate: 90.0,
      avgPackage: '₹8.5 LPA',
      highestPackage: '₹28 LPA',
      topRecruiter: 'Global Consulting'
    },
    {
      institution: 'Science Academy',
      totalStudents: 380,
      placed: 304,
      placementRate: 80.0,
      avgPackage: '₹5.8 LPA',
      highestPackage: '₹15 LPA',
      topRecruiter: 'Research Labs Inc'
    }
  ];

  const programWiseSuccess = [
    { program: 'B.Tech CSE', successRate: 92, avgTime: 45, students: 320 },
    { program: 'B.Tech ECE', successRate: 85, avgTime: 52, students: 280 },
    { program: 'MBA', successRate: 90, avgTime: 38, students: 180 },
    { program: 'MCA', successRate: 88, avgTime: 42, students: 150 },
    { program: 'B.Tech Mech', successRate: 82, avgTime: 58, students: 250 }
  ];

  const geographicDistribution = [
    { location: 'Mumbai', placements: 285, percentage: 22.8 },
    { location: 'Bangalore', placements: 342, percentage: 27.4 },
    { location: 'Delhi NCR', placements: 268, percentage: 21.5 },
    { location: 'Hyderabad', placements: 198, percentage: 15.9 },
    { location: 'Pune', placements: 155, percentage: 12.4 }
  ];

  const placementTimeline = [
    { phase: 'Pre-placement', students: 1248, percentage: 100 },
    { phase: 'Applied', students: 1180, percentage: 94.5 },
    { phase: 'Shortlisted', students: 856, percentage: 68.6 },
    { phase: 'Interviewed', students: 645, percentage: 51.7 },
    { phase: 'Offered', students: 512, percentage: 41.0 },
    { phase: 'Placed', students: 485, percentage: 38.9 }
  ];

  const COLORS = ['var(--color-primary)', 'var(--color-success)', 'var(--color-accent)', 'var(--color-warning)', 'var(--color-error)'];

  const handleExportStatistics = () => {
    const csvContent = [
      ['Institution', 'Total Students', 'Placed', 'Placement Rate', 'Avg Package', 'Highest Package'],
      ...institutionPerformance?.map(row => [
        row?.institution, 
        row?.totalStudents, 
        row?.placed, 
        `${row?.placementRate}%`, 
        row?.avgPackage, 
        row?.highestPackage
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `placement-statistics-${Date.now()}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <Select
          options={programOptions}
          value={selectedProgram}
          onChange={setSelectedProgram}
          className="w-full md:w-48"
        />
        <Select
          options={institutionOptions}
          value={selectedInstitution}
          onChange={setSelectedInstitution}
          className="w-full md:w-48"
        />
        <Button variant="outline" iconName="Download" onClick={handleExportStatistics}>
          Export Statistics
        </Button>
      </div>
      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Institution-wise Performance Comparison</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Institution</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Total Students</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Placed</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Placement Rate</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Avg. Package</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Highest Package</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Top Recruiter</th>
              </tr>
            </thead>
            <tbody>
              {institutionPerformance?.map((inst, index) => (
                <tr key={index} className="border-b border-border hover:bg-muted/50 transition-smooth">
                  <td className="py-3 px-4 text-sm font-medium text-card-foreground">{inst?.institution}</td>
                  <td className="py-3 px-4 text-sm text-card-foreground">{inst?.totalStudents}</td>
                  <td className="py-3 px-4 text-sm text-success font-medium">{inst?.placed}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden max-w-[100px]">
                        <div 
                          className="h-full bg-success transition-smooth"
                          style={{ width: `${inst?.placementRate}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-card-foreground">{inst?.placementRate}%</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-card-foreground">{inst?.avgPackage}</td>
                  <td className="py-3 px-4 text-sm font-medium text-primary">{inst?.highestPackage}</td>
                  <td className="py-3 px-4 text-sm text-muted-foreground">{inst?.topRecruiter}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Program-wise Success Rates</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={programWiseSuccess}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="program" stroke="var(--color-muted-foreground)" />
              <YAxis stroke="var(--color-muted-foreground)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }} 
              />
              <Bar dataKey="successRate" fill="var(--color-primary)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
          <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Geographic Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={geographicDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ location, percentage }) => `${location} (${percentage}%)`}
                outerRadius={100}
                fill="var(--color-primary)"
                dataKey="placements"
              >
                {geographicDistribution?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-card)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }} 
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Placement Timeline Analytics</h3>
        <div className="space-y-3">
          {placementTimeline?.map((phase, idx) => (
            <div key={idx} className="flex items-center gap-4">
              <div className="w-32 flex-shrink-0">
                <span className="text-sm font-medium text-card-foreground">{phase?.phase}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-8 bg-muted rounded-lg overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-primary to-accent flex items-center justify-end pr-3 transition-smooth"
                      style={{ width: `${phase?.percentage}%` }}
                    >
                      <span className="text-xs font-medium text-white">{phase?.students}</span>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-card-foreground w-16 text-right">{phase?.percentage}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
        <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Student Success Metrics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {programWiseSuccess?.map((program, idx) => (
            <div key={idx} className="p-4 rounded-lg border border-border hover:border-primary transition-smooth">
              <h4 className="text-sm font-medium text-card-foreground mb-3">{program?.program}</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Success Rate</span>
                  <span className="text-sm font-semibold text-success">{program?.successRate}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Avg. Time</span>
                  <span className="text-sm font-semibold text-card-foreground">{program?.avgTime} days</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Students</span>
                  <span className="text-sm font-semibold text-primary">{program?.students}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PlacementStatisticsTab;